import React, { useState } from "react";
import { ThumbsUp, ThumbsDown, AlertCircle, Send, Check } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

/**
 * Feedback widget shown alongside every AI-generated clinical output.
 * Captures thumbs up / thumbs down / partially correct + an optional
 * correction note that is logged via POST /api/feedback for continuous
 * improvement (HIPAA-safe: de-identified at the backend).
 */
export default function AIFeedback({ module, outputId, outputType = "ai_output", testid = "ai-feedback" }) {
  const [rating, setRating] = useState(null);     // up | down | partial
  const [open, setOpen] = useState(false);
  const [correction, setCorrection] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  const pick = (value) => {
    setRating(value);
    if (value === "up") {
      submit(value, "");
    } else {
      setOpen(true);
    }
  };

  const submit = async (value, note) => {
    setSubmitting(true);
    try {
      await api.post("/feedback", {
        module,
        output_id: outputId || null,
        output_type: outputType,
        rating: value,
        correction: note || null,
      });
      setSent(true);
      setOpen(false);
      toast.success("Thanks — feedback logged");
    } catch (e) {
      toast.error("Could not log feedback");
    } finally { setSubmitting(false); }
  };

  if (sent) {
    return (
      <div className="mt-3 flex items-center gap-2 text-xs text-emerald-700" data-testid={`${testid}-sent`}>
        <Check className="w-3.5 h-3.5" />
        Feedback logged. ARIA will adapt.
      </div>
    );
  }

  return (
    <div className="mt-3 border-t border-brand pt-3" data-testid={testid}>
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="text-[11px] uppercase tracking-[0.14em] text-brand-muted">
          Was this output helpful?
        </div>
        <div className="flex items-center gap-1.5">
          <button onClick={() => pick("up")}
            className={`px-2.5 py-1.5 rounded-md border text-xs inline-flex items-center gap-1.5 transition-colors ${rating==="up" ? "bg-emerald-50 border-emerald-300 text-emerald-700" : "border-brand text-brand-muted hover:bg-brand-bg"}`}
            data-testid={`${testid}-up`}>
            <ThumbsUp className="w-3.5 h-3.5" /> Helpful
          </button>
          <button onClick={() => pick("partial")}
            className={`px-2.5 py-1.5 rounded-md border text-xs inline-flex items-center gap-1.5 transition-colors ${rating==="partial" ? "bg-amber-50 border-amber-300 text-amber-700" : "border-brand text-brand-muted hover:bg-brand-bg"}`}
            data-testid={`${testid}-partial`}>
            <AlertCircle className="w-3.5 h-3.5" /> Partial
          </button>
          <button onClick={() => pick("down")}
            className={`px-2.5 py-1.5 rounded-md border text-xs inline-flex items-center gap-1.5 transition-colors ${rating==="down" ? "bg-red-50 border-red-300 text-red-700" : "border-brand text-brand-muted hover:bg-brand-bg"}`}
            data-testid={`${testid}-down`}>
            <ThumbsDown className="w-3.5 h-3.5" /> Incorrect
          </button>
        </div>
      </div>
      {open && (
        <div className="mt-3 space-y-2" data-testid={`${testid}-form`}>
          <label className="text-xs text-brand-muted">
            What was wrong? Your correction is logged de-identified and used to improve future drafts.
          </label>
          <textarea value={correction} onChange={(e) => setCorrection(e.target.value)}
            rows={3} placeholder="Suggest a correction or cite the source we got wrong…"
            className="w-full px-3 py-2 bg-white border border-brand rounded-md text-sm"
            data-testid={`${testid}-correction`} />
          <div className="flex gap-2 justify-end">
            <button onClick={() => { setOpen(false); setRating(null); }}
              className="text-xs px-3 py-1.5 rounded-md border border-brand hover:bg-brand-bg">
              Cancel
            </button>
            <button onClick={() => submit(rating, correction)} disabled={submitting}
              className="text-xs px-3 py-1.5 rounded-md bg-brand-primary text-white hover:bg-[#35096B] inline-flex items-center gap-1.5 disabled:opacity-60"
              data-testid={`${testid}-submit`}>
              <Send className="w-3.5 h-3.5" /> {submitting ? "Sending…" : "Send feedback"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
